import { React, useState, useEffect } from "react";
import { Button, Table, Input, notification, Modal, Switch, Row, Col, Typography } from "antd";
import { CheckOutlined, CloseOutlined, FilterOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import useRoleCheck from "utilities/hooks/roleCheck";
import axiosInstance from "utilities/axios";
import { fetchBrands } from "utilities/redux/slices/equipmentBrandsSlice";
import { fetchCategories } from "utilities/redux/slices/equipmentCategoriesSlice";

import EquipmentModal from "./components/EquipmentModal";

const { Text } = Typography;

const Equipment = () => {
  const dispatch = useDispatch();

  const { hasPermission } = useRoleCheck();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const [content, setContent] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/equipment", { params: { search } })
      .then((response) => {
        setContent(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [search]);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchBrands());
  }, [dispatch]);

  const columns = [
    {
      title: "Category",
      dataIndex: "category",
      width: 300,
      sorter: (a, b) => a.category.localeCompare(b.category),
    },
    {
      title: "Brand",
      dataIndex: "brand",
      sorter: (a, b) => a.brand.localeCompare(b.brand),
    },
    {
      title: "Model",
      dataIndex: "model",
      sorter: (a, b) => a.model.localeCompare(b.model),
    },
    {
      title: "Orderable",
      dataIndex: "orderableItem",
      align: "center",
      width: 50,
      responsive: ["md"],
      render: (orderableItem) =>
        orderableItem ? <CheckOutlined /> : <CloseOutlined />,
      filters: [
        {
          text: "Orderable",
          value: true,
        },
        {
          text: "Not Orderable",
          value: false,
        },
      ],
      onFilter: (value, record) => record.orderableItem === value
    },
    {
      title: "Calibratable",
      dataIndex: "calibrationAvailable",
      align: "center",
      responsive: ["md"],
      width: 50,
      render: (calibrationAvailable) =>
        calibrationAvailable ? <CheckOutlined /> : <CloseOutlined />,
      filters: [
        {
          text: "Calibratable",
          value: true,
        },
        {
          text: "Not Calibratable",
          value: false,
        },
      ],
      onFilter: (value, record) => record.calibrationAvailable === value
    },
    {
      title: "Detail",
      dataIndex: "",
      width: 100,
      key: "x",
      render: (text, record) => (
        <Button
          onClick={() => {
            setSelectedEquipment(record);
            setOpenModal(true);
          }}
        >
          Detail
        </Button>
      ),
    },
  ];

  //get page content

  return (
    <>
      <EquipmentModal
        equipment={selectedEquipment}
        open={openModal}
        onClose={() => {
          setSelectedEquipment(null);
          setOpenModal(false);
        }}
        onChange={() => {
          //refresh the page
          axiosInstance
            .get("/equipment", { params: { search } })
            .then((response) => {
              setContent(response.data);
              if (selectedEquipment) {
                setSelectedEquipment(
                  response.data.find(
                    (equipment) => equipment.id === selectedEquipment.id
                  )
                );
              }
            })
            .catch((error) => {
              notification.error({
                message: "Error",
                description: error.message,
              });
            });
          //get the current equipment data and set it to the selected equipment
        }}
      />
      <div className="p-8">
        <div className="flex">
          <Input.Search
            placeholder="Search Equipment"
            allowClear
            size="large"
            suffix="" // Remove the search icon
            value={search}
            onChange={(event) =>
              setSearchParams({ search: event.target.value })
            }
            className="mr-2"
          />
          {hasPermission(
            ["Audiology.Write", "Audiology.Admin"],
            [],
            null,
            false
          ) && (
            <Button
              type="primary"
              size="large"
              onClick={() => setOpenModal(true)}
            >
              Add Equipment
            </Button>
          )}
        </div>

        <div className="mt-4">
          <Table bordered columns={columns} dataSource={content} rowKey="id" />
        </div>
      </div>
    </>
  );
};

export default Equipment;
